'use client';

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface FileUploadProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onInput'> {
  /** Accepted file MIME types (e.g., "image/jpeg,image/png" or "video/mp4") */
  accept: string;
  /** Whether the upload is in progress */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Loading text to display */
  loadingText?: string;
  /** Callback when a file is selected */
  onFileSelect: (file: File) => void;
}

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      accept,
      isLoading = false,
      error,
      loadingText = 'Uploading...',
      onFileSelect,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
      const { files } = event.target as HTMLInputElement;
      const file = files?.item(0);
      if (file) {
        onFileSelect(file);
      }
    };

    return (
      <div className="space-y-2">
        <Input
          ref={ref}
          type="file"
          accept={accept}
          disabled={disabled || isLoading}
          onInput={handleInput}
          className={cn(className)}
          {...props}
        />
        {isLoading && <p className="text-muted-foreground text-xs">{loadingText}</p>}
        {error && <p className="text-destructive text-xs">{error}</p>}
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';
